"use client"

import * as React from "react"
import * as HeroIcons from "@heroicons/react/24/outline"

import { NavMain } from "components/nav-main"
import { NavSecondary } from "components/nav-secondary"
import { NavUser } from "components/nav-user"
import { useAuth } from "contexts/AuthContext"
import { MenuPermission } from "api/auth"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "components/ui/sidebar"

interface MenuItem {
  title: string
  url: string
  icon: any
  isActive?: boolean
  items?: {
    title: string
    url: string
  }[]
}

const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/shipments/in-transit';

const getIconComponent = (iconName: string) => {
  return (HeroIcons as any)[iconName] || HeroIcons.HomeIcon
}

const transformToNavMain = (permissions: MenuPermission[]): MenuItem[] => {
  if (!permissions || !Array.isArray(permissions)) return []

  return permissions
    .sort((a, b) => a.display_order - b.display_order)
    .map(item => {
      const parentRoutePath = item.route_path === '/' ? '/shipments/in-transit' : item.route_path
      
      const subItems = item.submenu && item.submenu.length > 0
        ? item.submenu
            .sort((a, b) => a.display_order - b.display_order)
            .map(subItem => ({
              title: subItem.menu_desc.charAt(0).toUpperCase() + subItem.menu_desc.slice(1),
              url: subItem.route_path,
            }))
        : undefined

      const isParentActive = currentPath === parentRoutePath || 
        (subItems?.some(sub => sub.url === currentPath) ?? false)

      return {
        title: item.menu_desc.charAt(0).toUpperCase() + item.menu_desc.slice(1),
        url: parentRoutePath,
        icon: getIconComponent(item.icon_name),
        isActive: isParentActive,
        items: subItems
      }
    })
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, permissions } = useAuth()

  const data = {
    user: {
      name: (user as any)?.full_name || 'User',
      email: (user as any)?.email || '',
      avatar: '',
    },
    navMain: transformToNavMain(permissions),
    navSecondary: [
     // add secondary nav items here to display on the bottom of the sidebar
    ],
  }

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href="/shipments/in-transit">
                <div className="grid flex-1 text-center text-sm leading-tight">
                  <img src="/logo.png" alt="MRC Logistics Logo" className="h-10 w-auto" />
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
        <NavSecondary items={data.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
